/**
 * The typeface catalogue behind the Display Preferences "Font" section.
 *
 * The site itself has ONE font — the --app-font-family / --app-font-size defaults
 * declared on :root in index.css and repeated in index.html's critical CSS. A user
 * may override both for their own account (DisplayPreferencesContext), and this
 * is the list they pick from.
 */

/** Shipped defaults — must match the :root block in index.css / index.html. */
export const DEFAULT_FONT_FAMILY = "Inter, 'Segoe UI', Tahoma, Arial, sans-serif";
export const DEFAULT_FONT_SIZE = 17;

export interface FontOption {
  label: string;
  /** The face we're actually trying to show — what availability is tested against. */
  primary: string;
  /** Full stack pushed into --app-font-family. */
  stack: string;
  group: string;
}

/**
 * Every stack names real fallbacks, and `primary` is checked against the machine's
 * installed fonts at runtime — the picker must never claim to show one face while
 * silently rendering another. Users are on different platforms: several of these
 * ship with Windows but not macOS.
 */
export const FONT_OPTIONS: FontOption[] = [
  // Self-hosted webfonts: identical on every machine, no install required.
  { group: 'Webfont (guaranteed)', label: 'Inter', primary: 'Inter', stack: "Inter, 'Segoe UI', Tahoma, Arial, sans-serif" },
  { group: 'Webfont (guaranteed)', label: 'General Sans', primary: 'General Sans', stack: "'General Sans', Inter, 'Segoe UI', Arial, sans-serif" },

  // Humanist / neo-grotesque UI faces that ship with Windows or Office.
  { group: 'Windows / Office', label: 'Arial Nova', primary: 'Arial Nova', stack: "'Arial Nova', Arial, 'Segoe UI', Helvetica, sans-serif" },
  { group: 'Windows / Office', label: 'Segoe UI', primary: 'Segoe UI', stack: "'Segoe UI', Tahoma, Arial, sans-serif" },
  { group: 'Windows / Office', label: 'Segoe UI Variable', primary: 'Segoe UI Variable', stack: "'Segoe UI Variable', 'Segoe UI', Tahoma, Arial, sans-serif" },
  { group: 'Windows / Office', label: 'Tahoma', primary: 'Tahoma', stack: "Tahoma, Verdana, Geneva, sans-serif" },
  { group: 'Windows / Office', label: 'Verdana', primary: 'Verdana', stack: "Verdana, Geneva, Tahoma, sans-serif" },
  { group: 'Windows / Office', label: 'Arial', primary: 'Arial', stack: "Arial, Helvetica, sans-serif" },
  { group: 'Windows / Office', label: 'Calibri', primary: 'Calibri', stack: "Calibri, Carlito, 'Segoe UI', Arial, sans-serif" },
  { group: 'Windows / Office', label: 'Candara', primary: 'Candara', stack: "Candara, 'Segoe UI', Optima, Arial, sans-serif" },
  { group: 'Windows / Office', label: 'Corbel', primary: 'Corbel', stack: "Corbel, 'Segoe UI', Arial, sans-serif" },
  { group: 'Windows / Office', label: 'Trebuchet MS', primary: 'Trebuchet MS', stack: "'Trebuchet MS', Tahoma, Verdana, sans-serif" },
  { group: 'Windows / Office', label: 'Franklin Gothic Book', primary: 'Franklin Gothic Book', stack: "'Franklin Gothic Book', 'Franklin Gothic', 'Arial Narrow', Arial, sans-serif" },
  { group: 'Windows / Office', label: 'Lucida Sans', primary: 'Lucida Sans Unicode', stack: "'Lucida Sans Unicode', 'Lucida Grande', 'Lucida Sans', Arial, sans-serif" },

  // macOS-native faces.
  { group: 'macOS', label: 'Helvetica Neue', primary: 'Helvetica Neue', stack: "'Helvetica Neue', Helvetica, Arial, sans-serif" },
  { group: 'macOS', label: 'Avenir Next', primary: 'Avenir Next', stack: "'Avenir Next', Avenir, 'Segoe UI', Arial, sans-serif" },
  { group: 'macOS', label: 'Lucida Grande', primary: 'Lucida Grande', stack: "'Lucida Grande', 'Lucida Sans Unicode', Arial, sans-serif" },

  // Whatever the OS picked for its own chrome.
  { group: 'Other', label: 'System default', primary: 'system-ui', stack: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif" },
];

/**
 * Is `family` actually installed/loaded on THIS machine?
 *
 * Measures a wide sample string in `family` against three very different generic
 * fallbacks. A missing face collapses onto the generic, so all three measurements
 * match it exactly; a present face differs from at least one. This is the standard
 * canvas-measurement trick — document.fonts.check() only knows about @font-face
 * rules and reports false for locally-installed system fonts.
 */
export function isFontAvailable(family: string, ctx?: CanvasRenderingContext2D | null): boolean {
  // `undefined` means "make your own"; an explicitly-passed null means "there is no
  // context" and must short-circuit rather than fall through to document.
  const context = ctx === undefined
    ? (typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d'))
    : ctx;
  if (!context) return false;
  if (family === 'system-ui') return true;
  const sample = 'mmmmmmmmmmlliWMIil0O1';
  const measure = (stack: string): number => {
    context.font = `72px ${stack}`;
    return context.measureText(sample).width;
  };
  return (['monospace', 'serif', 'sans-serif'] as const).some(
    generic => measure(`"${family}", ${generic}`) !== measure(generic)
  );
}
